import type { RoundCommandPayload } from "../round/round-commands.js";
import type { RoundResult, RoundSnapshot } from "../round/round-types.js";
import { RoundCommandError, RoundResultError } from "../round/round-errors.js";
import type {
  MeasuredAttempt,
  MeasuredChallengeRules,
} from "./measured-types.js";
import { validateMeasuredAttempt } from "./measured-validation.js";
import {
  isCountChallenge,
  isClosestChallenge,
  measuredActivities,
} from "./measured-activities.js";

export function selectMeasuredAttempt(
  snapshot: RoundSnapshot,
  participantId: string,
  attempt: number,
): MeasuredAttempt {
  return (
    snapshot.measuredAttempts?.[participantId]?.[attempt] ?? {
      status: "unset",
      value: null,
    }
  );
}

export function measuredValues(
  snapshot: RoundSnapshot,
  participantId: string,
): number[] {
  const rule = snapshot.rules.measuredChallenge;
  if (!rule) return [];
  const values = Array.from({ length: rule.attemptCount }, (_, index) =>
    selectMeasuredAttempt(snapshot, participantId, index + 1),
  )
    .filter((entry) => entry.status === "value")
    .map((entry) => entry.value!);
  return values.sort((a, b) =>
    isClosestChallenge(rule.activity) ? a - b : b - a,
  );
}

export function measuredProgress(snapshot: RoundSnapshot): {
  recorded: number;
  total: number;
} {
  const count = snapshot.rules.measuredChallenge?.attemptCount ?? 0;
  let recorded = 0;
  for (const p of snapshot.participants)
    for (let attempt = 1; attempt <= count; attempt++)
      if (selectMeasuredAttempt(snapshot, p.id, attempt).status !== "unset")
        recorded++;
  return { recorded, total: count * snapshot.participants.length };
}

export function setMeasuredAttempt(
  snapshot: RoundSnapshot,
  payload: RoundCommandPayload,
): RoundSnapshot {
  const rule = snapshot.rules.measuredChallenge;
  if (
    !rule ||
    snapshot.state !== "active" ||
    !("attemptNumber" in payload) ||
    !snapshot.participants.some((p) => p.id === payload.participantId) ||
    !Number.isInteger(payload.attemptNumber) ||
    payload.attemptNumber < 1 ||
    payload.attemptNumber > rule.attemptCount
  )
    throw new RoundCommandError(
      "This attempt is not editable in this challenge.",
    );
  validateMeasuredAttempt(rule, payload.entry);
  const next = {
    ...snapshot,
    version: snapshot.version + 1,
    measuredAttempts: {
      ...snapshot.measuredAttempts,
      [payload.participantId]: {
        ...snapshot.measuredAttempts?.[payload.participantId],
        [payload.attemptNumber]: { ...payload.entry },
      },
    },
  };
  const values = measuredValues(next, payload.participantId);
  next.metrics = {
    ...snapshot.metrics,
    [payload.participantId]: isCountChallenge(rule.activity)
      ? values.length
      : (values[0] ?? null),
  };
  return next;
}

export function calculateMeasuredResult(snapshot: RoundSnapshot): RoundResult {
  const rule = snapshot.rules.measuredChallenge!;
  const progress = measuredProgress(snapshot);
  if (progress.recorded !== progress.total)
    throw new RoundResultError(
      "Record every attempt, including misses, before finishing.",
    );
  const values = new Map(
    snapshot.participants.map((p) => [p.id, measuredValues(snapshot, p.id)]),
  );
  const ids = snapshot.participants
    .map((p) => p.id)
    .sort((a, b) => compare(values.get(a)!, values.get(b)!, rule));
  const best = values.get(ids[0]!)!;
  const winners =
    !isCountChallenge(rule.activity) && best.length === 0
      ? []
      : ids.filter((id) => compare(values.get(id)!, best, rule) === 0);
  return {
    calculationVersion: "2",
    format: "high-low",
    winnerIds: winners,
    tied: winners.length > 1,
    nassau: null,
    standings: ids.map((id) => ({
      targetId: id,
      gross: null,
      net: null,
      stableford: null,
      holesWon: null,
      metric: isCountChallenge(rule.activity)
        ? values.get(id)!.length
        : (values.get(id)![0] ?? null),
    })),
  };
}

function compare(
  a: number[],
  b: number[],
  rule: MeasuredChallengeRules,
): number {
  if (isCountChallenge(rule.activity)) return b.length - a.length;
  const length =
    rule.tieRule === "best-next" ? Math.max(a.length, b.length) : 1;
  for (let i = 0; i < length; i++) {
    if (a[i] === undefined && b[i] === undefined) return 0;
    if (a[i] === undefined) return 1;
    if (b[i] === undefined) return -1;
    const difference = isClosestChallenge(rule.activity)
      ? a[i]! - b[i]!
      : b[i]! - a[i]!;
    if (difference) return difference;
  }
  return 0;
}

export function measuredActivityLabel(rule: MeasuredChallengeRules): string {
  return measuredActivities[rule.activity].label;
}

export function measuredValueLabel(
  value: number | null,
  rule: MeasuredChallengeRules,
): string {
  return value === null
    ? "No valid attempt"
    : isCountChallenge(rule.activity)
      ? `${value}/${rule.attemptCount} ${rule.activity === "putting" ? "made" : rule.activity === "target-hits" ? "hits" : "successful"}`
      : `${Number(value.toFixed(2))} ${rule.unit}`;
}
